import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import {
  getClienteById,
  getLoncheadosByCliente,
  addCanalCliente,
  deleteCanalCliente,
  editarCliente,
  toggleActivoCliente,
} from '../../services/comercialClienteService';

const ACCENT = '#F97316';

// ── AREA CONFIG ───────────────────────────────────────────────────────────────
const AREA_CFG = {
  tienda: { label: 'TIENDA', color: '#34D399', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.25)' },
  distribucion: { label: 'DISTRIBUCIÓN', color: '#60A5FA', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.25)' },
  sala_loncheado: { label: 'SALA LONCHEADO', color: '#D6EF4E', bg: 'rgba(214,239,78,0.12)', border: 'rgba(214,239,78,0.25)' },
};

const CANAL_CFG = {
  telefono: { label: 'Teléfono', icon: 'Phone', color: '#34D399' },
  whatsapp: { label: 'WhatsApp', icon: 'MessageCircle', color: '#25D366' },
  email: { label: 'Email', icon: 'Mail', color: '#60A5FA' },
  instagram: { label: 'Instagram', icon: 'Instagram', color: '#E1306C' },
  facebook: { label: 'Facebook', icon: 'Facebook', color: '#1877F2' },
  web: { label: 'Web', icon: 'Globe', color: '#A78BFA' },
};

const AreaBadge = ({ area }) => {
  const cfg = AREA_CFG?.[area];
  if (!cfg) return null;
  return (
    <span className="inline-flex items-center font-oranienbaum text-xs px-2.5 py-1 rounded-full font-semibold tracking-wide"
      style={{ color: cfg?.color, background: cfg?.bg, border: `1px solid ${cfg?.border}` }}>
      {cfg?.label}
    </span>
  );
};

const Dash = () => <span className="text-white/25">—</span>;

const formatFecha = (ts) => {
  if (!ts) return null;
  try { return new Date(ts)?.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return null; }
};

// ── MODAL EDITAR ÁREAS ────────────────────────────────────────────────────────
const EditAreasModal = ({ cliente, onClose, onSaved }) => {
  const [areas, setAreas] = useState(
    cliente?.cliente_areas?.filter(a => a?.activo)?.map(a => a?.area) || []
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const toggleArea = (area) => {
    setAreas(prev => prev?.includes(area) ? prev?.filter(a => a !== area) : [...prev, area]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await editarCliente(cliente?.id, {
        datosGenerales: {
          nombre: cliente?.nombre,
          nombre_comercial: cliente?.nombre_comercial,
          tipo_persona: cliente?.tipo_persona,
          nif_cif: cliente?.nif_cif,
          telefono: cliente?.telefono,
          email: cliente?.email,
          direccion: cliente?.direccion,
          fecha_nacimiento: cliente?.fecha_nacimiento,
          notas_comerciales: cliente?.notas_comerciales,
          consentimiento_marketing: cliente?.consentimiento_marketing,
        },
        areas,
      });
      onSaved();
    } catch (err) {
      setError(err?.message || 'Error al guardar áreas.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-sm rounded-2xl overflow-hidden"
        style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-bold text-white">Editar Áreas</h3>
          <button onClick={onClose}><Icon name="X" size={16} className="text-white/40" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {Object.entries(AREA_CFG)?.map(([key, cfg]) => (
            <label key={key} className="flex items-center gap-3 cursor-pointer">
              <div className="w-5 h-5 rounded flex items-center justify-center"
                style={areas?.includes(key)
                  ? { background: cfg?.bg, border: `2px solid ${cfg?.color}` }
                  : { background: 'rgba(255,255,255,0.04)', border: '2px solid rgba(255,255,255,0.15)' }}>
                {areas?.includes(key) && <Icon name="Check" size={11} style={{ color: cfg?.color }} />}
              </div>
              <input type="checkbox" className="hidden" checked={areas?.includes(key)} onChange={() => toggleArea(key)} />
              <span className="font-oranienbaum text-sm" style={{ color: areas?.includes(key) ? cfg?.color : 'rgba(255,255,255,0.55)' }}>
                {cfg?.label}
              </span>
            </label>
          ))}
          {error && <p className="font-oranienbaum text-xs text-red-400">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 px-5 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="font-oranienbaum text-sm text-white/40 hover:text-white/70">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-oranienbaum text-sm font-semibold"
            style={{ background: ACCENT, color: '#0A0A0A', opacity: saving ? 0.7 : 1 }}>
            {saving ? <><Icon name="Loader2" size={13} className="animate-spin" />Guardando…</> : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── MODAL AÑADIR CANAL ────────────────────────────────────────────────────────
const AddCanalModal = ({ clienteId, onClose, onSaved }) => {
  const [canal, setCanal] = useState('telefono');
  const [identificador, setIdentificador] = useState('');
  const [nombreUsuario, setNombreUsuario] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!identificador?.trim()) { setError('El identificador es obligatorio.'); return; }
    setSaving(true);
    setError(null);
    try {
      await addCanalCliente(clienteId, {
        canal,
        identificador_externo: identificador,
        nombre_usuario: nombreUsuario || null,
      });
      onSaved();
    } catch (err) {
      setError(err?.message || 'Error al añadir canal.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-sm rounded-2xl overflow-hidden"
        style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-bold text-white">Añadir Canal</h3>
          <button onClick={onClose}><Icon name="X" size={16} className="text-white/40" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block font-oranienbaum text-white/50 text-xs mb-1">Canal</label>
            <select value={canal} onChange={e => setCanal(e?.target?.value)}
              className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
              {Object.entries(CANAL_CFG)?.map(([k, v]) => (
                <option key={k} value={k}>{v?.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-oranienbaum text-white/50 text-xs mb-1">
              {canal === 'email' ? 'Email' : canal === 'instagram' || canal === 'facebook' ? 'Usuario / URL' : 'Número / Identificador'}
            </label>
            <input value={identificador} onChange={e => setIdentificador(e?.target?.value)}
              className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
              placeholder="Ej: +34 600 000 000" />
          </div>
          <div>
            <label className="block font-oranienbaum text-white/50 text-xs mb-1">Nombre usuario (opcional)</label>
            <input value={nombreUsuario} onChange={e => setNombreUsuario(e?.target?.value)}
              className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
              placeholder="Nombre visible" />
          </div>
          {error && <p className="font-oranienbaum text-xs text-red-400">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 px-5 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="font-oranienbaum text-sm text-white/40 hover:text-white/70">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-oranienbaum text-sm font-semibold"
            style={{ background: ACCENT, color: '#0A0A0A', opacity: saving ? 0.7 : 1 }}>
            {saving ? <><Icon name="Loader2" size={13} className="animate-spin" />Guardando…</> : 'Añadir'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── TABS ──────────────────────────────────────────────────────────────────────
const TABS_BASE = ['RESUMEN', 'PEDIDOS', 'COMUNICACIONES', 'CAMPAÑAS', 'DATOS'];
const TABS_AREA = {
  tienda: 'TIENDA',
  distribucion: 'DISTRIBUCIÓN',
  sala_loncheado: 'SALA DE CORTE',
};

// ── TAB RESUMEN ───────────────────────────────────────────────────────────────
const TabResumen = ({ cliente }) => {
  const cards = [
    { label: 'Facturación total', value: '—', icon: 'TrendingUp', note: 'Pendiente de conexión' },
    { label: 'Última actividad', value: formatFecha(cliente?.updated_at) || '—', icon: 'Clock', note: null },
    { label: 'Pedidos', value: '—', icon: 'ShoppingCart', note: 'Pendiente de conexión' },
    { label: 'Servicios', value: '—', icon: 'Scissors', note: 'Pendiente de conexión' },
    { label: 'Conversaciones', value: '—', icon: 'MessageSquare', note: 'Pendiente de conexión' },
    { label: 'Última compra', value: '—', icon: 'Package', note: 'Pendiente de conexión' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {cards?.map(c => (
        <div key={c?.label} className="rounded-xl p-4"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Icon name={c?.icon} size={14} className="text-white/30" />
            <span className="font-oranienbaum text-white/40 text-xs uppercase tracking-wide">{c?.label}</span>
          </div>
          <p className="font-oranienbaum font-bold text-white text-xl">{c?.value}</p>
          {c?.note && <p className="font-oranienbaum text-white/20 text-xs mt-1">{c?.note}</p>}
        </div>
      ))}
    </div>
  );
};

// ── TAB SALA DE CORTE ─────────────────────────────────────────────────────────
const TabSalaCorte = ({ clienteId }) => {
  const [loncheados, setLoncheados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clienteId) return;
    setLoading(true);
    getLoncheadosByCliente(clienteId)?.then(data => { setLoncheados(data); setLoading(false); })?.catch(err => {
        console.error('[TabSalaCorte] error:', err);
        setError(err?.message || 'Error al cargar historial de sala.');
        setLoading(false);
      });
  }, [clienteId]);

  if (loading) return (
    <div className="flex items-center gap-3 py-8">
      <div className="w-4 h-4 rounded-full border-2 border-white/10 border-t-yellow-300 animate-spin" />
      <span className="font-oranienbaum text-white/40 text-sm">Cargando historial…</span>
    </div>
  );

  if (error) return (
    <div className="rounded-xl px-4 py-3 font-oranienbaum text-sm"
      style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#F87171' }}>
      Error: {error}
    </div>
  );

  if (loncheados?.length === 0) return (
    <div className="text-center py-12">
      <Icon name="Scissors" size={32} className="text-white/15 mx-auto mb-3" />
      <p className="font-oranienbaum text-white/40 text-sm">Sin trabajos de sala registrados para este cliente.</p>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Scissors" size={14} style={{ color: '#D6EF4E' }} />
        <span className="font-oranienbaum text-white/60 text-xs uppercase tracking-widest">Últimos trabajos</span>
        <span className="px-2 py-0.5 rounded-full font-oranienbaum text-xs"
          style={{ background: 'rgba(214,239,78,0.08)', color: '#D6EF4E', border: '1px solid rgba(214,239,78,0.20)' }}>
          {loncheados?.length}
        </span>
      </div>
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="grid grid-cols-7 gap-2 px-4 py-2.5"
          style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          {['Fecha', 'Producto', 'Tipo', 'Peso', 'Rendimiento', 'Blísteres', 'Estado']?.map(h => (
            <div key={h} className="font-oranienbaum text-white/35 text-xs uppercase tracking-wide">{h}</div>
          ))}
        </div>
        {loncheados?.map((l, idx) => (
          <div key={l?.id}
            className="grid grid-cols-7 gap-2 px-4 py-3"
            style={{ borderBottom: idx < loncheados?.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
            <div className="font-oranienbaum text-white/60 text-xs">{formatFecha(l?.fecha_produccion || l?.created_at) || '—'}</div>
            <div className="font-oranienbaum text-white/80 text-xs">{l?.productos?.nombre || '—'}</div>
            <div className="font-oranienbaum text-white/50 text-xs capitalize">{l?.tipo || '—'}</div>
            <div className="font-oranienbaum text-white/60 text-xs">{l?.peso ? `${l?.peso} kg` : '—'}</div>
            <div className="font-oranienbaum text-xs"
              style={{ color: l?.rendimiento ? (l?.rendimiento >= 70 ? '#34D399' : l?.rendimiento >= 50 ? '#FBBF24' : '#F87171') : 'rgba(255,255,255,0.25)' }}>
              {l?.rendimiento ? `${Number(l?.rendimiento)?.toFixed(1)}%` : '—'}
            </div>
            <div className="font-oranienbaum text-white/50 text-xs">{l?.numero_blisteres ?? '—'}</div>
            <div className="font-oranienbaum text-white/40 text-xs">{l?.estado_rendimiento || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── TAB DISTRIBUCIÓN ──────────────────────────────────────────────────────────
const TabDistribucion = () => (
  <div>
    <div className="rounded-xl p-5 mb-4"
      style={{ background: 'rgba(96,165,250,0.05)', border: '1px solid rgba(96,165,250,0.15)' }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon name="Info" size={14} style={{ color: '#60A5FA' }} />
        <span className="font-oranienbaum text-blue-400 text-sm font-semibold">Información de distribución pendiente de conexión</span>
      </div>
      <p className="font-oranienbaum text-white/40 text-xs">
        Las relaciones de pedidos y facturación de distribución se conectarán en la siguiente fase.
      </p>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {[
        { label: 'Pedidos', icon: 'ShoppingCart' },
        { label: 'Facturación', icon: 'FileText' },
        { label: 'Pendiente de cobro', icon: 'Clock' },
        { label: 'Último pedido', icon: 'Calendar' },
        { label: 'Productos habituales', icon: 'Package' },
      ]?.map(c => (
        <div key={c?.label} className="rounded-xl p-4"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Icon name={c?.icon} size={13} className="text-white/25" />
            <span className="font-oranienbaum text-white/35 text-xs uppercase tracking-wide">{c?.label}</span>
          </div>
          <p className="font-oranienbaum font-bold text-white/30 text-xl">—</p>
        </div>
      ))}
    </div>
  </div>
);

// ── TAB TIENDA ────────────────────────────────────────────────────────────────
const TabTienda = () => (
  <div>
    <div className="rounded-xl p-5 mb-4"
      style={{ background: 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.15)' }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon name="Info" size={14} style={{ color: '#34D399' }} />
        <span className="font-oranienbaum text-emerald-400 text-sm font-semibold">Datos de tienda pendientes de conexión</span>
      </div>
      <p className="font-oranienbaum text-white/40 text-xs">
        La vinculación cliente-ticket Epelsa se construirá cuando exista una identificación fiable.
      </p>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {[
        { label: 'Compras', icon: 'ShoppingBag' },
        { label: 'Facturación', icon: 'FileText' },
        { label: 'Ticket medio', icon: 'TrendingUp' },
        { label: 'Última compra', icon: 'Calendar' },
        { label: 'Productos habituales', icon: 'Package' },
      ]?.map(c => (
        <div key={c?.label} className="rounded-xl p-4"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Icon name={c?.icon} size={13} className="text-white/25" />
            <span className="font-oranienbaum text-white/35 text-xs uppercase tracking-wide">{c?.label}</span>
          </div>
          <p className="font-oranienbaum font-bold text-white/30 text-xl">—</p>
        </div>
      ))}
    </div>
  </div>
);

// ── TAB PEDIDOS ───────────────────────────────────────────────────────────────
const TabPedidos = () => (
  <div className="text-center py-12">
    <Icon name="ShoppingCart" size={32} className="text-white/15 mx-auto mb-3" />
    <p className="font-oranienbaum text-white/40 text-sm">Historial de pedidos pendiente de conexión.</p>
  </div>
);

// ── TAB COMUNICACIONES ────────────────────────────────────────────────────────
const TabComunicaciones = () => (
  <div className="text-center py-12">
    <Icon name="MessageSquare" size={32} className="text-white/15 mx-auto mb-3" />
    <p className="font-oranienbaum text-white/40 text-sm">Bandeja omnicanal pendiente de conexión.</p>
    <p className="font-oranienbaum text-white/20 text-xs mt-1">WhatsApp · Instagram · Facebook · Email</p>
  </div>
);

// ── TAB CAMPAÑAS ──────────────────────────────────────────────────────────────
const TabCampanas = () => (
  <div className="text-center py-12">
    <Icon name="Megaphone" size={32} className="text-white/15 mx-auto mb-3" />
    <p className="font-oranienbaum text-white/40 text-sm">Campañas pendientes de conexión.</p>
  </div>
);

// ── TAB DATOS ─────────────────────────────────────────────────────────────────
const TabDatos = ({ cliente, onCanalAdded, onCanalDeleted }) => {
  const [addModal, setAddModal] = useState(false);
  const canales = cliente?.cliente_canales || [];

  return (
    <div className="space-y-6">
      {/* Datos generales */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-4 rounded-full" style={{ background: ACCENT }} />
          <span className="font-oranienbaum text-white/60 text-xs uppercase tracking-widest">Datos Generales</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: 'Nombre', value: cliente?.nombre },
            { label: 'Nombre comercial', value: cliente?.nombre_comercial },
            { label: 'Tipo persona', value: cliente?.tipo_persona },
            { label: 'NIF / CIF', value: cliente?.nif_cif },
            { label: 'Teléfono', value: cliente?.telefono },
            { label: 'Email', value: cliente?.email },
            { label: 'Dirección', value: cliente?.direccion },
            { label: 'Fecha nacimiento', value: formatFecha(cliente?.fecha_nacimiento) },
          ]?.map(f => (
            <div key={f?.label} className="rounded-lg px-3 py-2.5"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="font-oranienbaum text-white/35 text-xs mb-0.5">{f?.label}</p>
              <p className="font-oranienbaum text-white/80 text-sm">{f?.value || '—'}</p>
            </div>
          ))}
        </div>
        {cliente?.notas_comerciales && (
          <div className="mt-3 rounded-lg px-3 py-2.5"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="font-oranienbaum text-white/35 text-xs mb-0.5">Notas comerciales</p>
            <p className="font-oranienbaum text-white/70 text-sm">{cliente?.notas_comerciales}</p>
          </div>
        )}
      </div>

      {/* Canales de contacto */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 rounded-full" style={{ background: '#60A5FA' }} />
            <span className="font-oranienbaum text-white/60 text-xs uppercase tracking-widest">Canales de Contacto</span>
          </div>
          <button onClick={() => setAddModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-oranienbaum text-xs font-semibold transition-all hover:opacity-90"
            style={{ background: 'rgba(96,165,250,0.12)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.25)' }}>
            <Icon name="Plus" size={11} />
            Añadir canal
          </button>
        </div>
        {canales?.length === 0 ? (
          <div className="rounded-xl px-4 py-6 text-center"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="font-oranienbaum text-white/30 text-sm">Sin canales registrados.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {canales?.map(canal => {
              const cfg = CANAL_CFG?.[canal?.canal] || { label: canal?.canal, icon: 'Link', color: '#fff' };
              return (
                <div key={canal?.id} className="flex items-center justify-between rounded-lg px-4 py-3"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: `${cfg?.color}15`, border: `1px solid ${cfg?.color}30` }}>
                      <Icon name={cfg?.icon} size={14} style={{ color: cfg?.color }} />
                    </div>
                    <div>
                      <p className="font-oranienbaum text-white/80 text-sm">{canal?.identificador_externo}</p>
                      <p className="font-oranienbaum text-white/35 text-xs">{cfg?.label}{canal?.nombre_usuario ? ` · ${canal?.nombre_usuario}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canal?.principal && (
                      <span className="font-oranienbaum text-xs px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(249,115,22,0.10)', color: ACCENT, border: '1px solid rgba(249,115,22,0.20)' }}>
                        Principal
                      </span>
                    )}
                    <button onClick={() => onCanalDeleted(canal?.id)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-500/10 transition-colors">
                      <Icon name="Trash2" size={12} className="text-white/30 hover:text-red-400" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Marketing */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-4 rounded-full" style={{ background: '#A78BFA' }} />
          <span className="font-oranienbaum text-white/60 text-xs uppercase tracking-widest">Marketing</span>
        </div>
        <div className="rounded-lg px-4 py-3 flex items-center gap-3"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="w-5 h-5 rounded flex items-center justify-center"
            style={cliente?.consentimiento_marketing
              ? { background: 'rgba(167,139,250,0.15)', border: '2px solid #A78BFA' }
              : { background: 'rgba(255,255,255,0.04)', border: '2px solid rgba(255,255,255,0.15)' }}>
            {cliente?.consentimiento_marketing && <Icon name="Check" size={11} style={{ color: '#A78BFA' }} />}
          </div>
          <span className="font-oranienbaum text-white/60 text-sm">
            {cliente?.consentimiento_marketing ? 'Autoriza comunicaciones comerciales' : 'No autoriza comunicaciones comerciales'}
          </span>
          {cliente?.fecha_consentimiento_marketing && (
            <span className="font-oranienbaum text-white/25 text-xs ml-auto">
              {formatFecha(cliente?.fecha_consentimiento_marketing)}
            </span>
          )}
        </div>
      </div>

      {addModal && (
        <AddCanalModal
          clienteId={cliente?.id}
          onClose={() => setAddModal(false)}
          onSaved={() => { setAddModal(false); onCanalAdded(); }}
        />
      )}
    </div>
  );
};

// ── FICHA 360° ────────────────────────────────────────────────────────────────
const ComercialClienteFicha = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('RESUMEN');
  const [editAreasModal, setEditAreasModal] = useState(false);

  const cargar = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getClienteById(id);
      setCliente(data);
    } catch (err) {
      console.error('[ComercialClienteFicha] cargar error:', err);
      setError(err?.message || 'Error al cargar el cliente.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const areasActivas = cliente?.cliente_areas?.filter(a => a?.activo)?.map(a => a?.area) || [];

  // Construir tabs dinámicos
  const tabs = ['RESUMEN'];
  if (areasActivas?.includes('tienda')) tabs?.push('TIENDA');
  if (areasActivas?.includes('distribucion')) tabs?.push('DISTRIBUCIÓN');
  if (areasActivas?.includes('sala_loncheado')) tabs?.push('SALA DE CORTE');
  tabs?.push('PEDIDOS', 'COMUNICACIONES', 'CAMPAÑAS', 'DATOS');

  // Asegurar que el tab activo es válido
  useEffect(() => {
    if (!tabs?.includes(activeTab)) setActiveTab('RESUMEN');
  }, [areasActivas?.join(',')]);

  const handleCanalDeleted = async (canalId) => {
    try {
      await deleteCanalCliente(canalId);
      cargar();
    } catch (err) {
      console.error('[ComercialClienteFicha] deleteCanalCliente error:', err);
    }
  };

  const handleToggleActivo = async () => {
    if (!cliente) return;
    try {
      await toggleActivoCliente(cliente?.id, !cliente?.activo);
      cargar();
    } catch (err) {
      console.error('[ComercialClienteFicha] toggleActivo error:', err);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      <div className="flex items-center gap-3">
        <div className="w-6 h-6 rounded-full border-2 border-white/10 border-t-orange-400 animate-spin" />
        <span className="font-oranienbaum text-white/40">Cargando ficha…</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      <div className="rounded-xl px-6 py-5 max-w-md w-full"
        style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
        <p className="font-oranienbaum text-red-400 font-semibold mb-1">Error al cargar cliente</p>
        <p className="font-oranienbaum text-white/50 text-sm">{error}</p>
        <button onClick={() => navigate('/comercial-clientes')}
          className="mt-4 font-oranienbaum text-sm text-white/50 hover:text-white/80">
          ← Volver a clientes
        </button>
      </div>
    </div>
  );

  if (!cliente) return null;

  return (
    <div className="min-h-screen px-4 py-6 md:px-8"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs font-oranienbaum text-white/30 mb-5">
        <button onClick={() => navigate('/comercial-clientes')} className="hover:text-white/60 transition-colors">
          🤝 Comercial
        </button>
        <Icon name="ChevronRight" size={12} />
        <button onClick={() => navigate('/comercial-clientes')} className="hover:text-white/60 transition-colors">
          Clientes
        </button>
        <Icon name="ChevronRight" size={12} />
        <span className="text-white/60">{cliente?.nombre}</span>
      </nav>

      {/* Cabecera */}
      <div className="rounded-2xl p-5 mb-5"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(249,115,22,0.10)', border: '1px solid rgba(249,115,22,0.20)' }}>
              <span className="font-oranienbaum font-bold text-orange-400 text-xl">
                {cliente?.nombre?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="font-oranienbaum font-bold text-white text-xl leading-tight">{cliente?.nombre}</h1>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-oranienbaum text-xs font-semibold`}
                  style={cliente?.activo
                    ? { background: 'rgba(52,211,153,0.10)', color: '#34D399', border: '1px solid rgba(52,211,153,0.20)' }
                    : { background: 'rgba(239,68,68,0.08)', color: '#F87171', border: '1px solid rgba(239,68,68,0.20)' }}>
                  <span className={`w-1.5 h-1.5 rounded-full ${cliente?.activo ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  {cliente?.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              {cliente?.nombre_comercial && (
                <p className="font-oranienbaum text-white/50 text-sm mb-1">{cliente?.nombre_comercial}</p>
              )}
              <div className="flex items-center gap-2 flex-wrap text-xs font-oranienbaum text-white/40">
                {cliente?.tipo_persona && <span className="capitalize">{cliente?.tipo_persona}</span>}
                {cliente?.nif_cif && <><span>·</span><span>{cliente?.nif_cif}</span></>}
                {cliente?.telefono && (
                  <><span>·</span>
                  <span className="flex items-center gap-1"><Icon name="Phone" size={10} />{cliente?.telefono}</span></>
                )}
                {cliente?.email && (
                  <><span>·</span>
                  <span className="flex items-center gap-1 truncate max-w-[200px]"><Icon name="Mail" size={10} />{cliente?.email}</span></>
                )}
              </div>
              {/* Badges áreas */}
              <div className="flex items-center gap-2 flex-wrap mt-2">
                {areasActivas?.length > 0
                  ? areasActivas?.map(a => <AreaBadge key={a} area={a} />)
                  : <span className="font-oranienbaum text-white/25 text-xs">Sin área asignada</span>}
                <button onClick={() => setEditAreasModal(true)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full font-oranienbaum text-xs transition-all hover:opacity-80"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.40)' }}>
                  <Icon name="Pencil" size={9} />
                  Editar áreas
                </button>
              </div>
            </div>
          </div>
          {/* Acciones */}
          <div className="flex items-center gap-2">
            <button onClick={handleToggleActivo}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-oranienbaum text-xs transition-all hover:opacity-80"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.50)' }}>
              <Icon name={cliente?.activo ? 'EyeOff' : 'Eye'} size={12} />
              {cliente?.activo ? 'Desactivar' : 'Activar'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap mb-5 overflow-x-auto pb-1">
        {tabs?.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg font-oranienbaum text-xs font-semibold whitespace-nowrap transition-all`}
            style={activeTab === tab
              ? { background: ACCENT, color: '#0A0A0A' }
              : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.50)' }}>
            {tab}
          </button>
        ))}
      </div>

      {/* Contenido tab */}
      <div>
        {activeTab === 'RESUMEN' && <TabResumen cliente={cliente} />}
        {activeTab === 'TIENDA' && <TabTienda />}
        {activeTab === 'DISTRIBUCIÓN' && <TabDistribucion />}
        {activeTab === 'SALA DE CORTE' && <TabSalaCorte clienteId={cliente?.id} />}
        {activeTab === 'PEDIDOS' && <TabPedidos />}
        {activeTab === 'COMUNICACIONES' && <TabComunicaciones />}
        {activeTab === 'CAMPAÑAS' && <TabCampanas />}
        {activeTab === 'DATOS' && (
          <TabDatos
            cliente={cliente}
            onCanalAdded={cargar}
            onCanalDeleted={handleCanalDeleted}
          />
        )}
      </div>

      {/* Modal editar áreas */}
      {editAreasModal && (
        <EditAreasModal
          cliente={cliente}
          onClose={() => setEditAreasModal(false)}
          onSaved={() => { setEditAreasModal(false); cargar(); }}
        />
      )}
    </div>
  );
};

export default ComercialClienteFicha;
